/**
 * TikTok Pixel helpers.
 *
 * Complete Registration is fired from the same success-path gate as Meta
 * (`trackRegistrationSuccess`): once per brand-new account, after identify.
 * Phone is intentionally omitted until collected.
 *
 * Pass raw identity values — the TikTok browser SDK SHA-256 hashes them.
 */

export function identifyTikTokUser({ email, externalId } = {}) {
  if (typeof window === "undefined") {
    return { identified: false, reason: "no_window" };
  }

  const payload = {};
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedExternalId = String(externalId || "").trim();

  if (normalizedEmail) {
    payload.email = normalizedEmail;
  }
  if (normalizedExternalId) {
    payload.external_id = normalizedExternalId;
  }

  if (!Object.keys(payload).length) {
    return { identified: false, reason: "missing_identity" };
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "tiktok_advanced_matching",
    tiktok_email: payload.email || undefined,
    tiktok_external_id: payload.external_id || undefined,
  });

  if (typeof window.ttq?.identify !== "function") {
    return { identified: false, reason: "ttq_unavailable", payload };
  }

  window.ttq.identify(payload);
  return { identified: true, payload };
}

/**
 * Fires the standard CompleteRegistration pixel event after a real signup.
 * Prefer this over Events Manager click/URL rules so the event is eligible
 * for Lead Generation optimization.
 *
 * @param {{ eventId?: string }} [options]
 * @returns {{ tracked: boolean, reason?: string }}
 */
export function trackTikTokCompleteRegistration({ eventId } = {}) {
  if (typeof window === "undefined") {
    return { tracked: false, reason: "no_window" };
  }

  const normalizedEventId = String(eventId || "").trim();
  const properties = {
    content_name: "Account Registration",
  };
  const trackOptions = normalizedEventId
    ? { event_id: normalizedEventId }
    : undefined;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "tiktok_complete_registration",
    tiktok_event_id: normalizedEventId || undefined,
  });

  if (typeof window.ttq?.track !== "function") {
    return { tracked: false, reason: "ttq_unavailable" };
  }

  if (trackOptions) {
    window.ttq.track("CompleteRegistration", properties, trackOptions);
  } else {
    window.ttq.track("CompleteRegistration", properties);
  }

  return { tracked: true, eventId: normalizedEventId || undefined };
}
